import React, { ReactNode } from "react";

import {
  CheckboxProps,
  useCheckbox,
  Stack,
  Box,
  Flex,
  Text,
  Icon,
} from "@chakra-ui/react";
import { CheckIcon } from "@heroicons/react/outline";

type CheckboxCardProps = CheckboxProps & { tag?: ReactNode, index: number };

const CheckboxCard = React.forwardRef<HTMLInputElement, CheckboxCardProps>(({
  tag,
  index,
  ...props
}, ref) => {
  const { getInputProps, getCheckboxProps, getLabelProps } = useCheckbox(props);

  const input = getInputProps(undefined, ref);
  const checkbox = getCheckboxProps();
  const label = getLabelProps();

  const isChecked = checkbox?.["data-checked"] !== undefined;
  const isHovered = checkbox?.["data-hover"] !== undefined;
  const isFocus = checkbox?.["data-focus"] !== undefined;
  const isInvalid = props.isInvalid;

  const isHoveredOrFocus = isHovered || isFocus;

  return (
    <Box as="label" maxWidth="2xl" id={`${props?.name}.${index}`}>
      <input {...input} required={false} />
      <Flex
        {...checkbox}
        justify="space-between"
        ring="1px"
        rounded="lg"
        w="full"
        ringColor="gray.200"
        px="5"
        py="4"
        ml={0}
        alignItems="baseline"
        cursor="pointer"
        color="darkBrand"
        bgColor="white"
        _disabled={{
          cursor: "not-allowed",
          pointerEvents: "none"
        }}
        _checked={{
          bgColor: "brand",
          color: "white",
          ringColor: "brand",
          ring: "2px",
        }}
        _focus={{
          ringColor: "brand",
        }}
        _hover={{
          ringColor: "brand",
        }}
        _invalid={{
          bgColor: "white",
          color: "invalid.500",
          ringColor: "invalid.500",
          _hover: {
            ringColor: "brand",
          },
          _focus: {
            ringColor: "brand",
          }
        }}
      >
        <Stack
          spacing="3"
          direction="row"
          align="center"
        >
          <Flex
            flexShrink={0}
            border="2px"
            borderColor={isChecked || isHoveredOrFocus ? "brand" : isInvalid ? "invalid.500" : "gray.200"}
            bgColor="white"
            rounded="md"
            justify="center"
            alignItems="center"
            width="5"
            height="5"
          >
            <Icon
              as={CheckIcon}
              display={isChecked ? "inline-block" : "none"}
              color={isChecked ? "brand" : "white"}
              w="4"
              h="4"
            />
          </Flex>
          <Text
            color={isChecked ? "white" : isHoveredOrFocus ? "brand" : isInvalid ? "invalid.500" : "gray.500"}
            fontWeight={isChecked ? "medium" : "base"}
            {...label}
          >
            {props.children}
          </Text>
        </Stack>
        {tag}
      </Flex>
    </Box>
  );
});

export default CheckboxCard;
